import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../share-components/AppContext';
import "./OrderAvailableProduct.scss";
import { Button, Card } from 'react-bootstrap';
import { customStorage, formatDateTime, getPayloadData, isNullOrEmptyString, scrollToFormError } from '../../services/UtilityService';
import { DELI_TYPE_DEFINE, DELI_TYPES, ORDER_STATUS, ORDER_STATUS_DEFINE, SELECT_DEFAULT, SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import { editDeliInfo, getOrderDetails } from '../../services/ManagementService';
import { ROUTE_ORDER_DETAILS } from '../../constants/routeConstants';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import AlertDialog from '../../share-components/AlertDialog';
import FormTitle from '../../share-components/FormTitle';
import noImage from '../../assets/No_Image_Available.jpg';
import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';

const OrderDeliEdit = () => {

    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const { orderId } = useParams();
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [originalOrderCartData, setOriginalOrderCartData] = useState([]);
    const [subtotalAmount, setSubtotalAmount] = useState(0);
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [paymentReceivedAmount, setPaymentReceivedAmount] = useState(0);
    const [orderInfo, setOrderInfo] = useState(null);
    const [formValues, setFormValues] = useState(null);
    const [showDeliInfoEditConfirmDialog, setShowDeliInfoEditConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        setRouteNaviData("ORDER >> EDIT DELIVERY INFO");
    }, []);

    const fetchOrderDetails = async () => {
        if ((parseInt(orderId) || 0) <= 0) {
            return;
        }
        setLoading(true);
        try {
            // const response = await getAdminAccounts() || [];
            const response = await getOrderDetails(orderId);
            const orderInfo = response?.orderInfo;

            if (isNullOrEmptyString(orderInfo?.deliveredById) || orderInfo?.deliveredById !== currentUser.userId) {
                navigate(`${ROUTE_ORDER_DETAILS}/${orderId}`);
            }

            setDeliveryFee(parseInt(orderInfo?.deliveryFee) || 0);

            const initialFormValues = {
                deliveryType: orderInfo.deliveryType,
                deliveryFee: orderInfo.deliveryFee,
                shippingTrackerLink: orderInfo.shippingTrackerLink
            }
            setFormValues(initialFormValues);

            const orderCart = response.orderCartData || [];

            const totalOrderAmount = (orderCart).reduce((sum, order) => sum + (parseInt(order.salePrice) * parseInt(order.orderedQuantity)), 0) || 0;
            setSubtotalAmount(totalOrderAmount);

            setOriginalOrderCartData(orderCart);
            setOrderInfo(orderInfo);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrderDetails();
    }, [orderId]);

    useEffect(() => {
        const receivedAmount = (parseInt(orderInfo?.paymentReceivedAmount) || 0) - (parseInt(orderInfo?.deliveryFee) || 0) + (parseInt(deliveryFee) || 0);
        setPaymentReceivedAmount(receivedAmount);
    }, [deliveryFee]);

    const initialValues = {
        deliveryType: formValues?.deliveryType || '',
        deliveryFee: formValues?.deliveryFee || '',
        shippingTrackerLink: formValues?.shippingTrackerLink || '',
        validatePaymentReceivedAmount: ''
    };

    const validationSchema = Yup.object({
        deliveryType: Yup.string().nullable()
            .required('Delivery Type is required.'),
        deliveryFee: Yup.number().nullable()
            .max(100000, 'Amount must not exceed 100000¥.')
            .when("deliveryType", {
                is: (value) => (parseInt(value) || null) === DELI_TYPE_DEFINE.prepaidDelivery.value,
                then: (schema) => schema.required("Delivery Fee is required."),
                otherwise: (schema) => schema.nullable()
            }),
        validatePaymentReceivedAmount: Yup.string().nullable()
            .test(
                "payment-check",
                "(Delivery Fee + Subtotal Price) must not be less than PR Gift Price.",
                function (value) {
                    // const { paymentReceivedAmount } = this.options.context; // get from Formik validationSchema context
                    if ((parseInt(paymentReceivedAmount, 10) || 0) < 0) {
                        return false;
                    }
                    return true;
                }
            ),
        // deliveryFee: Yup.number().nullable()
        //     .max(100000, 'Amount must not exceed 100000¥.')
        //     .when("deliveryType", {
        //         is: (value) => (parseInt(value) || null) === DELI_TYPE_DEFINE.prepaidDelivery.value,
        //         then: (schema) => schema.required("Delivery Fee is required."),
        //         otherwise: (schema) => schema.nullable()
        //     }),
        shippingTrackerLink: Yup.string().nullable()
            .max(500, 'Shipping Tracker Link must not exceed 500 characters.'),
    });

    const onSubmit = async (form) => {
        setFormValues(form);
        setShowDeliInfoEditConfirmDialog(true);
    };

    const handleConfirmEdit = async () => {
        try {
            setShowDeliInfoEditConfirmDialog(false);
            setLoading(true);
            const formData = {
                orderId: parseInt(orderId, 10) || 0,
                deliveryType: parseInt(formValues?.deliveryType) || null,
                deliveryFee: parseInt(formValues?.deliveryFee) || 0,
                shippingTrackerLink: formValues?.shippingTrackerLink || null,
            }
            await editDeliInfo(formData);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = async () => {
        setShowSuccessDialog(false);
        navigate(`${ROUTE_ORDER_DETAILS}/${orderId}`);
    }

    const checkIsRequireDeliveredBy = (orderStatus) => {
        return parseInt(orderStatus) === ORDER_STATUS_DEFINE.shipped.value || parseInt(orderStatus) === ORDER_STATUS_DEFINE.finished.value
    }

    const checkIsRequiredPaymentReceivedBy = (orderStatus) => {
        return parseInt(orderStatus) === ORDER_STATUS_DEFINE.paid.value || parseInt(orderStatus) === ORDER_STATUS_DEFINE.shipped.value || parseInt(orderStatus) === ORDER_STATUS_DEFINE.finished.value;
    }

    const checkIsRequireDeliveryFee = (deliveryType) => {
        return (parseInt(deliveryType) || null) === DELI_TYPE_DEFINE.prepaidDelivery.value
    }

    const getShowData = (orderCartItem) => {
        return (<div>
            <div style={{
                fontWeight: '600',
                display: 'flex',
            }}>
                <div style={{ flex: "1", paddingRight: "10px" }}>
                    <div><i className='bi bi-award'></i> {orderCartItem?.brandName}</div><hr className='m-0' />
                    <div><i className='bi bi-box'></i> {orderCartItem?.productName}</div><hr className='m-0' />
                    <div><i className='bi bi-tags'></i> {orderCartItem?.categoryName}</div><hr className='m-0' />
                    {(!isNullOrEmptyString(orderCartItem?.size) || !isNullOrEmptyString(orderCartItem.color)) &&
                        <>
                            <div>
                                <i className='bi bi-palette'></i> {!isNullOrEmptyString(orderCartItem.color) && <span>[{orderCartItem.color}]</span>}{!isNullOrEmptyString(orderCartItem?.size) && <span>[{orderCartItem.size}]</span>}

                            </div><hr className='m-0' />
                        </>
                    }
                    <div><i className='bi bi-cash'></i> {orderCartItem?.salePrice}¥ x({orderCartItem?.orderedQuantity})</div><hr className='m-0' />
                </div>
                <div style={{ paddingTop: "5px" }}>
                    <img
                        src={orderCartItem?.productPicture ? `data:image/jpeg;base64,${orderCartItem?.productPicture}` : noImage}
                        alt="picture"
                        className="img-thumbnail"
                        style={{ width: "var(--orderProductPictureWidth)", height: "var(--orderProductPictureHeight)", objectFit: "cover" }}
                    />
                </div>
            </div>
        </div>
        )
    };

    const getOrderCartCount = () => {
        const totalOrderCount = originalOrderCartData?.reduce((sum, item) => sum + item.orderedQuantity, 0);
        return totalOrderCount || 0;
    }

    const handleDeliveryTypeChange = (value, setFieldValue, validateForm) => {
        const inputDeliType = parseInt(value) || 0;
        if (inputDeliType !== DELI_TYPE_DEFINE.prepaidDelivery.value) {
            setFieldValue("deliveryFee", "");
            setDeliveryFee("");
            setTimeout(() => {
                validateForm();
            }, 50);
        }
    }

    return (
        <div id='detailsOrderPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"Edit Delivery Info"} isCenter={true} />

            {isFirstLoaded && <>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validate={(values) => {
                        setFormValues(values);
                    }}
                    onSubmit={onSubmit}>
                    {({ values, setFieldValue, setFieldTouched, errors, isValid, dirty, validateForm }) => {
                        const firstError = !dirty ? "Fill the required fields." : Object.values(errors)[0];
                        return (
                            <Form>
                                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                                    <Card.Body className='pt-2'>
                                        <div className="container">
                                            <div className="row justify-content-center">
                                                <div className="" style={{ maxWidth: '550px' }}>
                                                    <div className='mb-3 center-block-column'>
                                                        {originalOrderCartData.map((orderCart, index) => (
                                                            <div key={index} className='info-card'
                                                            // style={{ animationDelay: `${index * 0.6}s` }}
                                                            >
                                                                {getShowData(orderCart)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div style={{
                                                        padding: '5px',
                                                        background: 'chartreuse',
                                                        borderRadius: '15px',
                                                        textAlign: 'center',
                                                        fontWeight: 'bold',
                                                        fontSize: '1rem',
                                                        marginBottom: '15px'
                                                    }}>
                                                        <span>Ordered ({getOrderCartCount()} item{(getOrderCartCount()) > 1 ? "s" : ""})</span>
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Customer Name</label>
                                                        <div className="w-100 label-box main-description">
                                                            {orderInfo?.customerName}
                                                        </div>
                                                    </div>

                                                    {!isNullOrEmptyString(orderInfo?.customerAddress?.trim()) && <div className="mb-1">
                                                        <label className="custom-label">Customer Address</label>
                                                        <div className="w-100 label-box">
                                                            {orderInfo?.customerAddress}
                                                        </div>
                                                    </div>}

                                                    <div className="mb-1">
                                                        <label className="custom-label">Order Status</label>
                                                        <div className="w-100 label-box main-description">
                                                            {ORDER_STATUS.find(status => status.value === parseInt(orderInfo?.orderStatus))?.text}
                                                        </div>
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Ordered By</label>
                                                        <div className="w-100 label-box main-description">
                                                            {orderInfo?.orderedBy}
                                                        </div>
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Ordered Date</label>
                                                        <div className="w-100 label-box">
                                                            {formatDateTime(orderInfo?.createdDateTime)}
                                                        </div>
                                                    </div>

                                                    {orderInfo?.createdById !== orderInfo?.lastUpdatedById && <div className="mb-1">
                                                        <label className="custom-label">Last Updated By</label>
                                                        <div className="w-100 label-box">
                                                            {orderInfo?.lastUpdatedBy}
                                                        </div>
                                                    </div>}

                                                    {orderInfo?.createdDateTime !== orderInfo.lastUpdatedDateTime && <div className="mb-1">
                                                        <label className="custom-label">Last Updated Date</label>
                                                        <div className="w-100 label-box">
                                                            {formatDateTime(orderInfo?.lastUpdatedDateTime)}
                                                        </div>
                                                    </div>}

                                                    <div className="mb-1">
                                                        <label className="custom-label">Delivery Type*</label>
                                                        <Field as="select" name="deliveryType" className="w-100 customInputControl"
                                                            onChange={(e) => {
                                                                const inputValue = e.target.value;
                                                                setFieldValue("deliveryType", inputValue);
                                                                handleDeliveryTypeChange(inputValue, setFieldValue, validateForm);
                                                            }}
                                                        >
                                                            <option value="">{SELECT_DEFAULT}</option>
                                                            {DELI_TYPES.map(deliType => (
                                                                <option key={deliType.value} value={deliType.value}>{deliType.text}</option>
                                                            ))}
                                                        </Field>
                                                        <ErrorMessage name="deliveryType" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    {/* {(parseInt(paymentReceivedAmount, 10) || 0) < 0 && <div className="formErrDiv text-danger">
                                                        PR Gift Price must not exceed (Delivery Fee + Subtotal Price)
                                                    </div>} */}

                                                    {checkIsRequireDeliveryFee(values.deliveryType) && <div className="mb-1">
                                                        <label className="custom-label">Delivery Fee</label>
                                                        <Field name="deliveryFee" type="number"
                                                            inputMode="numeric"  // forces numeric keypad on mobile
                                                            pattern="[0-9]*"      // enforces only digits
                                                            className="w-100 customInputControl clearNumberSpin"
                                                            onKeyDown={(e) => {
                                                                if (e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                const inputValue = e.target.value;
                                                                setDeliveryFee(inputValue);
                                                                setFieldValue("deliveryFee", inputValue);
                                                            }}
                                                            onWheel={(e) => e.target.blur()}
                                                        />
                                                        <ErrorMessage name="deliveryFee" component="div" className="formErrDiv text-danger" />
                                                    </div>}

                                                    {checkIsRequireDeliveredBy(orderInfo?.orderStatus) && <div className="mb-1">
                                                        <label className="custom-label">Delivered By</label>
                                                        <div className="w-100 label-box main-description">
                                                            {orderInfo?.deliveredBy}
                                                        </div>
                                                    </div>}

                                                    <div className="mb-1">
                                                        <label className="custom-label">Shipping Tracker Link</label>
                                                        <Field name="shippingTrackerLink" component="textarea" rows="3" className="w-100 customInputControl" />
                                                        <ErrorMessage name="shippingTrackerLink" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Subtotal Price</label>
                                                        <div className="w-100 label-box">
                                                            {subtotalAmount}¥
                                                        </div>
                                                    </div>

                                                    {orderInfo?.serviceCharge > 0 && <div className="mb-1">
                                                        <label className="custom-label">Service Charge</label>
                                                        <div className="w-100 label-box">
                                                            {orderInfo?.serviceCharge}¥
                                                        </div>
                                                    </div>}

                                                    {orderInfo?.tipMoney > 0 && <div className="mb-1">
                                                        <label className="custom-label">Tip Money</label>
                                                        <div className="w-100 label-box">
                                                            {orderInfo?.tipMoney}¥
                                                        </div>
                                                    </div>}

                                                    <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2" >
                                                        <label className="form-check-label custom-label">
                                                            PR Gift Include?
                                                            <input type="checkbox" checked={orderInfo?.prGiftFlag === true} className="form-check-input ms-2" disabled />
                                                        </label>
                                                    </div>

                                                    {orderInfo?.prGiftFlag === true && <div className="mb-1">
                                                        <label className="custom-label">PR Gift Price</label>
                                                        <div className="w-100 label-box">
                                                            {orderInfo?.prGiftAmount}¥
                                                        </div>
                                                    </div>}

                                                    <div className="mb-1">
                                                        <label className="custom-label">Payment Received Amount</label>
                                                        <div className="w-100 label-box main-description">
                                                            {paymentReceivedAmount}¥
                                                        </div>
                                                        <ErrorMessage name="validatePaymentReceivedAmount" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    {checkIsRequiredPaymentReceivedBy(orderInfo?.orderStatus) &&
                                                        <>
                                                            <div className="mb-1">
                                                                <label className="custom-label">Payment Received By</label>
                                                                <div className="w-100 label-box main-description">
                                                                    {orderInfo?.paymentReceivedBy}
                                                                </div>
                                                            </div>

                                                            <div className="mb-1">
                                                                <label className="custom-label">Received Bank Name</label>
                                                                <div className="w-100 label-box">
                                                                    {orderInfo?.receivedBankName}
                                                                </div>
                                                            </div>
                                                        </>
                                                    }

                                                    <div className="mb-1">
                                                        <label className="custom-label">Comment</label>
                                                        <div className="w-100 label-box input-area">
                                                            {orderInfo?.comment}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                    <div className='floating-bottom-btn-group'>
                                        <div className='floating-bottom-btn-grid'>
                                            <Button
                                                className="appBtn w-100"
                                                type="submit"
                                                variant="primary"
                                                onClick={scrollToFormError}
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            </Form>
                        );
                    }}
                </Formik>
                <ConfirmDialog
                    dialogId='deliInfoEditConfirmDialog'
                    showDialog={showDeliInfoEditConfirmDialog}
                    // title='Logout Dialog'
                    body='Are you sure to edit Delivery Info?'
                    onConfirm={handleConfirmEdit}
                    onCancel={() => { setShowDeliInfoEditConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={`Delivery Info is successfully edited.`}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default OrderDeliEdit;